<template>
  <div class="ion-toggle form-group" :class="'size-' + size">
    <div class="row-center-gap-2 font-normal" :class="labelSizeMap[size]">
      <span v-if="inactiveLabel" class="color-text-light">
        {{ inactiveLabel }}
      </span>
      <IonToggle
        :checked="initialValue === activeValue"
        :inactive-value="inactiveValue"
        :active-value="activeValue"
        :color="activeColor"
        :aria-label="ariaLabel"
        @ionChange="onChange"
        :disabled="isDisabled"
      />
      <span v-if="activeLabel" class="color-text-light">
        {{ activeLabel }}
      </span>
    </div>
    <div v-if="isFormField">
      <Field v-model="inputValue" :id="inputId" :name="inputId" hidden />
      <ErrorMessage :name="inputId" class="validation-message" />
    </div>
  </div>
</template>

<script lang="ts">
import { LABEL_FONT } from "@/constants/sizing";
import { useAppSettingsStore } from "@/stores/core/appSettingsStore";
import { ComponentSizeType } from "@/types/componentType";
import { IonToggle } from "@ionic/vue";
import { storeToRefs } from "pinia";
import { ErrorMessage, Field } from "vee-validate";
import { PropType, defineComponent, ref } from "vue";

export default defineComponent({
  name: "CustomIonSwitch",
  components: { IonToggle, Field, ErrorMessage },
  props: {
    inputId: { type: String, required: true },
    // Accessible name forwarded onto IonToggle (Ionic inherits aria-label only from
    // the ion-toggle element itself). Absent ⇒ attribute omitted, render unchanged.
    ariaLabel: { type: String, default: undefined },
    inactiveValue: { type: [Number, Boolean], default: false },
    activeValue: { type: [Number, Boolean], default: true },
    initialValue: { type: [Number, Boolean], default: false },
    inactiveLabel: { type: String },
    activeLabel: { type: String },
    activeColor: { type: String, default: "primary" },
    isFormField: { type: Boolean, default: false },
    isDisabled: { type: Boolean, default: false },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
  },
  setup(props, { emit }) {
    const appSettingsStore = useAppSettingsStore();
    const { IsTabletScreen } = storeToRefs(appSettingsStore);
    const inputValue = ref<boolean>(props.initialValue === props.activeValue);

    function onChange(event: CustomEvent) {
      inputValue.value = event.detail.checked;
      emit("onChange", inputValue.value ? props.activeValue : props.inactiveValue);
    }
    // Label tracks the shared control-adjacent label ladder (xxs/xs/s).
    return { inputValue, IsTabletScreen, labelSizeMap: LABEL_FONT, onChange };
  },
});
</script>
<style lang="scss" scoped>
.ion-toggle {
  // Thumb diameter draws from the shared --toggle-size-* geometry ladder (Role E).
  --toggle-size: var(--toggle-size-default);
  --toggle-gap: 2px;
  --toggle-height: calc(var(--toggle-size) + var(--toggle-gap) * 2);
  --toggle-width: calc(var(--toggle-size) * 2.25);
  &.size-small {
    --toggle-size: var(--toggle-size-small);
  }
  &.size-large {
    --toggle-size: var(--toggle-size-large);
  }
  ion-toggle {
    --handle-max-height: auto;
    --handle-width: var(--toggle-size);
    --handle-height: var(--toggle-size);
    --handle-border-radius: calc(var(--toggle-size) / 2);
    --border-radius: calc(var(--toggle-height) / 2);
    --handle-spacing: var(--toggle-gap);
    --handle-transition: var(--motion-duration-normal) var(--motion-ease-in-out);
  }
  ion-toggle::part(track) {
    width: var(--toggle-width);
    height: var(--toggle-height);
    overflow: visible;
  }
}
</style>
